import { CurrencyPipe } from '@angular/common';
import {
    Component,
    Inject,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IMaskModule } from 'angular-imask';
import { map, Observable, tap } from 'rxjs';
import { AccessConstants } from '../../../../../../../../core/constants/access-constants';
import { nameOf } from '../../../../../../../../core/functions/string-helpers';
import { FormGroupType } from '../../../../../../../../core/models/form-group-type';
import { CardTypeIconComponent } from '../../../../../../../../shared/components/card-type-icon/card-type-icon.component';
import { DraggableModalWrapperComponent } from '../../../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { UserStateService } from '../../../../../../../user/shared/services/user-state/user-state.service';
import { PaymentService } from '../../../../../shared/services/payment/payment.service';
import { PaymentTransactionMaskConfig } from '../../../../constants/payment-transaction-mask-config';
import { CreditInformationResponse } from '../../../../models/credit-information-response';
import { CreditPaymentRequest } from '../../../../models/credit-payment-request';
import { CreditPaymentFormMessages } from './constants/credit-payment-form-messages';
import { CreditPaymentFormDialogModalData } from './models/credit-payment-form-dialog-modal-data';
import { CreditPaymentFormModel } from './models/credit-payment-form-model';
import { CreditPaymentFormService } from './services/credit-payment-form.service';

@Component({
    selector: 'app-application-credit-payment-form-dialog',
    templateUrl: `./credit-payment-form-dialog.component.html`,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatDialogContent,
        MatSelectModule,
        MatDialogActions,
        MatDialogTitle,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        IMaskModule,
        DraggableModalWrapperComponent,
        CardTypeIconComponent,
        CurrencyPipe,
    ],
})
export class CreditPaymentFormDialogComponent implements OnInit {
    form: FormGroup<FormGroupType<CreditPaymentFormModel>> = {} as FormGroup<
        FormGroupType<CreditPaymentFormModel>
    >;
    creditInformationResponse: CreditInformationResponse | null = null;

    paymentTransactionAmountMaskConfig = PaymentTransactionMaskConfig.amount;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: CreditPaymentFormDialogModalData,
        private readonly paymentService: PaymentService,
        private readonly userStateService: UserStateService,
        private readonly creditPaymentFormService: CreditPaymentFormService,
        private readonly dialogRef: MatDialogRef<CreditPaymentFormDialogComponent>,
    ) {}

    ngOnInit(): void {
        this.loadForm().subscribe();
    }

    /**
     * If the form is valid, this method generates a request object from the form values and
     * sends it to the API to save the credit payment information. Upon successful completion
     * of the API call, it closes the dialog and can optionally pass a result indicating success.
     */
    save(): void {
        if (
            this.form.valid &&
            this.hasRemainingAmountToCredit() &&
            this.isCreditOperationPermitted()
        ) {
            this.saveFormDataToApi(
                this.data.orderId,
                this.data.paymentId,
                this.generateRequestObject(),
            )
                .pipe(
                    // After successfully saving the form data to the API, we can close the dialog
                    // and pass a result indicating success
                    tap(() => this.dialogRef.close(true)),
                )
                .subscribe();
        }
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    /**
     * This method checks if there is remaining credit available to be credited back to the customer.
     * It returns true if the creditInformationResponse is not null and the remainingCredit is greater than 0.
     *
     * @returns A boolean value indicating whether there is remaining credit available to credit back to the customer.
     */
    hasRemainingAmountToCredit(): boolean {
        return (
            !!this.creditInformationResponse &&
            this.creditInformationResponse.remainingCredit > 0
        );
    }

    /**
     * This method checks if the currently authenticated user has the necessary permissions to perform credit operations.
     * Only the most elevated roles are allowed to perform credit operations.
     *
     * @returns A boolean value indicating whether the user has permission to perform credit operations.
     */
    isCreditOperationPermitted(): boolean {
        const authenticatedUser = this.userStateService.getUser();

        if (!authenticatedUser) {
            throw new Error('User is not authenticated');
        }

        return AccessConstants.RolesThatCanManageAllPayments.includes(
            authenticatedUser.role,
        );
    }

    getAmountMaxError(): string | undefined {
        const errors = this.form.controls.amount.errors;

        if (!errors) {
            return undefined;
        }

        return errors[
            nameOf<typeof CreditPaymentFormMessages>(
                'amountExceedsCreditRemaining',
            )
        ] as string | undefined;
    }

    private loadForm(): Observable<void> {
        return this.getFormDataFromApi(
            this.data.orderId,
            this.data.paymentId,
        ).pipe(
            // Storing the API response in a component property for later use, such as displaying credit information in the template
            tap((response) => (this.creditInformationResponse = response)),

            // Assigning the form definition with the maximum amount based on the remaining credit from the API response
            tap(
                (response) =>
                    (this.form =
                        this.creditPaymentFormService.getFormDefinition(
                            response.remainingCredit,
                        )),
            ),

            map(() => void 0),
        );
    }

    private getFormDataFromApi(
        orderId: string,
        paymentId: string,
    ): Observable<CreditInformationResponse> {
        return this.paymentService.getCreditInformation(orderId, paymentId);
    }

    private saveFormDataToApi(
        orderId: string,
        paymentId: string,
        request: CreditPaymentRequest,
    ): Observable<void> {
        return this.paymentService.credit(orderId, paymentId, request);
    }

    private generateRequestObject(): CreditPaymentRequest {
        return {
            ...this.form.value,
        } as CreditPaymentRequest;
    }
}
